import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import FullpageWrapper from '@/components/FullpageWrapper';
import IframeContainer from '@/components/IframeContainer';
import { loadLocation, setIsNewsTickerVisible } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import AchievementFeed from '@/components/AchievementFeed';
import FloatingHeaderBar from '@/components/FloatingHeaderBar';
import ProfileDrawer from '@/components/ProfileDrawer';
import MediaViewer from '@/components/MediaViewer';
import NewsTicker from '@/components/NewsTicker';
import InformationDrawer from '@/components/InformationDrawer';
import ExitModal from '@/components/ExitModal';
import ShareModal from '@/components/ShareModal';
import { sendPageViewEvent } from '@/utils/analytics/events';
import { homePath } from '@/theme/vars';
import { useTheme } from '@/theme';
import { heartbeat } from '@/arise/api';

import * as styles from './styles.module.scss';

export default function LocationPageBasic() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { location, isLocationLoading, isProfileOpen, isInformationOpen, locationLoadErrorId, shouldHideUi } =
        useAppSelector((state) => state.app);
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);
    const { locationId: id } = useParams();

    useEffect(() => {
        // Redirect if not found
        if (locationLoadErrorId === id) {
            navigate(homePath);
        }
    }, [locationLoadErrorId]);

    useEffect(() => {
        if ((!location || location.id !== id) && !isLocationLoading) dispatch(loadLocation(id));
        sendPageViewEvent();

        // Reset UI on load
        dispatch(setIsNewsTickerVisible(!!theme.customJSON.shouldNewsTickerShowImmediately));
    }, [id]);

    useEffect(() => {
        let interval;
        if (id && loggedIn) {
            heartbeat(id);
            interval = setInterval(() => {
                heartbeat(id);
            }, 30 * 1000); // 30s
        }
        return () => {
            clearInterval(interval);
        };
    }, [id, loggedIn]);

    if (!location || location.id !== id) return <></>;

    const sidebarIsOpen = (isProfileOpen || isInformationOpen) && !shouldHideUi;
    const hasProfileDrawer = !process.env.PUBLIC_DISABLE_PROFILE;
    const hasInformationDrawer = theme.customJSON?.informationDrawer?.enabled;
    const hasNewsTicker = !process.env.PUBLIC_DISABLE_NEWSTICKER;
    const hasMenu = !process.env.PUBLIC_DISABLE_MENU;

    const arisewareLoadingImageUrl = location.customJSON?.arisewareLoadingImageUrl;

    return (
        <FullpageWrapper pageHasProfileDrawer={hasProfileDrawer}>
            <div
                className={classNames(styles.contentWrapper, {
                    [styles['contentWrapper--sidebarOpen']]: sidebarIsOpen,
                })}
            >
                <IframeContainer
                    forceInFrontOfUi={shouldHideUi}
                    src={location.iframeURL || location.latestArisewareBuildURL}
                    arisewareProjectID={location.arisewareProjectID}
                    onReady={() => {}}
                    loadingImage={arisewareLoadingImageUrl ? arisewareLoadingImageUrl : null}
                    loadingText={theme.metadata?.customJSON?.loadingText}
                />
                {hasNewsTicker && <NewsTicker />}
                {hasMenu && <FloatingHeaderBar />}
            </div>
            <div
                className={classNames(styles.sidebarWrapper, {
                    [styles['sidebarWrapper--sidebarOpen']]: sidebarIsOpen,
                })}
            >
                {hasProfileDrawer && <ProfileDrawer />}
                {hasInformationDrawer && <InformationDrawer />}
            </div>
            <MediaViewer />
            <AchievementFeed />
            <ShareModal />
            <ExitModal />
        </FullpageWrapper>
    );
}
